"use client";

// Public marketing landing — rendered by app/page.tsx for signed-out visitors
// (and expired tokens) in place of the dashboard. AppShell already bows out
// for "/" (see app-shell.tsx), so this file owns the whole page: nav, hero,
// features, footer.
//
// Visual source of truth: design-system/mockups/ (desktop/mobile × dark/light).
// Copy and structure follow the approved spec verbatim; mobile (<768px) is a
// genuine content variant, not a squeezed desktop — so both variants render
// and the breakpoint toggles which is visible (hidden md:block / md:hidden).
//
// Color comes exclusively from the --lp-* landing tokens (globals.css): the
// .landing class on the root below picks the light set, .dark .landing the
// dark set. No second token system, no hard-coded theme colors — the few
// literal hexes are the colored logo PLACEHOLDERS (spec: swap for real SVG
// assets later) and the mobile progress circles' approved hexes.
//
// Reused app components: StatCard (hero mockup KPIs), ArcDonut (job-type
// donut), the motion system (motion.ts) and usePrefersReducedMotion. The
// theme toggle is the app-shell's useTheme() contract with landing styling.
// Desktop is flat per the mockup — no parallax, no scroll spine.

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  BriefcaseIcon,
  BuildingIcon,
  ChevronDownIcon,
  ClipboardIcon,
  GlobeIcon,
  MoonIcon,
  PencilSquareIcon,
  SearchIcon,
  SparklesIcon,
  SunIcon,
} from "@/app/components/icons";
import { ArcDonut, type ArcDonutDatum } from "@/app/components/arc-donut";
import { StatCard } from "@/app/components/stat-card";
import { DUR_SLOW, EASE_SETTLE, stagger } from "@/app/components/motion";
import { usePrefersReducedMotion } from "@/app/lib/use-reduced-motion";
import { useTheme } from "@/app/components/theme-provider";
import { cn } from "@/app/lib/cn";

// ---------------------------------------------------------------------------
// Motion — landing-local pure helpers (exported for the tests to pin).
// Same contract as motion.ts: transform + opacity only, reduced motion
// collapses to instant, delays stagger by list position.
// ---------------------------------------------------------------------------

export function landingVariants(reduced: boolean, y = 18) {
  return {
    hidden: { opacity: 0, y: reduced ? 0 : y },
    visible: { opacity: 1, y: 0 },
  };
}

export function landingTransition(index = 0, reduced = false) {
  return {
    duration: reduced ? 0 : DUR_SLOW,
    ease: EASE_SETTLE,
    delay: reduced ? 0 : stagger(index),
  };
}

// Fades a section up the first time it scrolls into view. `index` spaces
// siblings; `once` keeps the mockup's charts from replaying on every pass.
function Reveal({
  index = 0,
  className,
  children,
}: {
  index?: number;
  className?: string;
  children: React.ReactNode;
}) {
  const reduced = usePrefersReducedMotion();
  return (
    <motion.div
      variants={landingVariants(reduced)}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      transition={landingTransition(index, reduced)}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Copy + data — verbatim from the approved spec.
// ---------------------------------------------------------------------------

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

const DESKTOP_SOURCES = ["LinkedIn", "StepStone", "Indeed", "XING", "Glassdoor"];
// Mobile wraps the same sources over two lines with a trailing "+ more".
const MOBILE_SOURCES_LINE_1 = ["LinkedIn", "StepStone", "Indeed"];
const MOBILE_SOURCES_LINE_2 = ["XING", "Glassdoor"];

const HERO_STATS = [
  { big: "100K+", caption: "jobs scanned daily" },
  { big: "10x faster", caption: "to apply" },
  { big: "Higher reply rate", caption: "with tailored applications" },
];

type PillTone = "green" | "blue" | "purple";

const PILL_TONES: Record<PillTone, string> = {
  green: "bg-lp-pill-green-bg text-lp-pill-green-text",
  blue: "bg-lp-pill-blue-bg text-lp-pill-blue-text",
  purple: "bg-lp-pill-purple-bg text-lp-pill-purple-text",
};

// Colored squares stand in for real company logos (spec: the user swaps in
// real SVG assets later). Hexes are the mockups' placeholder fills.
function LogoSquare({
  label,
  bg,
  darkText = false,
  className,
}: {
  label: string;
  bg: string;
  darkText?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      style={{ backgroundColor: bg }}
      className={cn(
        "flex flex-none items-center justify-center rounded-button font-bold",
        darkText ? "text-[#1f2937]" : "text-white",
        className
      )}
    >
      {label}
    </span>
  );
}

interface MatchDatum {
  initial: string;
  bg: string;
  darkText?: boolean;
  role: string;
  org?: string;
  pct: number;
  tone: PillTone;
  time?: string;
}

// Desktop mockup rows carry company · city and a relative time; the mobile
// card's narrower rows are role + fit pill only (per the mockups).
const DESKTOP_MATCHES: MatchDatum[] = [
  {
    initial: "S",
    bg: "#0066f9",
    role: "Supply Chain Manager",
    org: "BMW Group · Munich, Germany",
    pct: 94,
    tone: "green",
    time: "2 hours ago",
  },
  {
    initial: "DB",
    bg: "#e2001a",
    role: "Logistics Project Manager",
    org: "Deutsche Bahn · Berlin, Germany",
    pct: 87,
    tone: "green",
    time: "5 hours ago",
  },
  {
    initial: "DHL",
    bg: "#ffcc00",
    darkText: true,
    role: "Operations Planner",
    org: "DHL · Leipzig, Germany",
    pct: 82,
    tone: "green",
    time: "1 day ago",
  },
];

const MOBILE_MATCHES: MatchDatum[] = [
  { initial: "S", bg: "#0066f9", role: "Supply Chain Manager", pct: 94, tone: "green" },
  { initial: "DB", bg: "#e2001a", role: "Project Manager", pct: 87, tone: "blue" },
  {
    initial: "DHL",
    bg: "#ffcc00",
    darkText: true,
    role: "Operations Planner",
    pct: 82,
    tone: "blue",
  },
];

// Job-type donut: exhaustive categories (101 ≈ 100), so ArcDonut's default
// arcTotal (segment sum) gives a full ring. Shades of --lp-primary, darkest →
// lightest by value — one palette per theme because SVG stroke needs literal
// colors; selected post-mount via useTheme (same mounted-state pattern the
// rest of the app uses for theme/reduced-motion reads).
const JOB_TYPE_SPLIT = [
  { label: "Full-time", value: 68 },
  { label: "Contract", value: 15 },
  { label: "Part-time", value: 12 },
  { label: "Internship", value: 6 },
];

const DONUT_SHADES: Record<"dark" | "light", string[]> = {
  dark: ["#0057ff", "#3d7bff", "#7fa7ff", "#bcd0ff"],
  light: ["#005bfe", "#4d84fe", "#94b7fe", "#cddcff"],
};

// "Jobs discovered per day": 13 daily bars (Apr 28 → May 10), gridlines at
// 10/20/30, x labels every third bar. Rising shape per the mockup.
const DISCOVERED_BARS = [8, 12, 10, 15, 13, 18, 16, 21, 19, 24, 22, 27, 31];
const DISCOVERED_TICKS = [10, 20, 30];
const BAR_INDEX_LABELS: Record<number, string> = {
  0: "Apr 28",
  3: "May 1",
  6: "May 4",
  9: "May 7",
  12: "May 10",
};
const BAR_SCALE_MAX = 34;

const SIDEBAR_NAV = [
  { label: "Dashboard", active: true },
  { label: "Jobs", active: false },
  { label: "Companies", active: false },
  { label: "Interview Prep", active: false },
  { label: "Documents", active: false },
  { label: "Settings", active: false },
];

const CV_CHECKS = ["Tailored to the job", "Optimized keywords", "Clean, modern format"];

const FEATURE_ROWS = [
  { title: "Interview prep", subtitle: "Practice real questions.", icon: ClipboardIcon },
  { title: "Application tracker", subtitle: "Track every step.", icon: BriefcaseIcon },
  { title: "Company insights", subtitle: "See the context fast.", icon: BuildingIcon },
];

// Mobile Smart-matching bars: approved circle hexes + fit percentages. The
// pill tints come from the matching --lp-pill-* tokens.
const MOBILE_FIT_BARS = [
  { pct: 94, circle: "#05c594", tone: "green" as PillTone },
  { pct: 87, circle: "#0066f9", tone: "blue" as PillTone },
  { pct: 82, circle: "#6440f2", tone: "purple" as PillTone },
];

const FOOTER_LINKS = [
  { label: "About", href: "#" },
  { label: "Privacy", href: "#" },
  { label: "Contact", href: "#" },
];

// ---------------------------------------------------------------------------
// Buttons — pill shapes per spec: primary solid --lp-primary with trailing
// arrow, secondary on --lp-surface behind a 1px --lp-border.
// ---------------------------------------------------------------------------

const PRIMARY_PILL =
  "group inline-flex items-center justify-center gap-2 rounded-pill bg-lp-primary font-semibold text-white transition-colors duration-150 hover:bg-lp-accent";

const SECONDARY_PILL =
  "inline-flex items-center justify-center rounded-pill border border-lp-border bg-lp-surface font-semibold text-lp-ink transition-colors duration-150 hover:border-lp-ink-2/60";

function PillArrow() {
  return (
    <span
      aria-hidden="true"
      className="transition-transform duration-150 group-hover:translate-x-0.5"
    >
      →
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared header pieces
// ---------------------------------------------------------------------------

// The app-shell ThemeToggle is square (lg:h-8 w-8 rounded-md); the landing
// spec calls for a circular icon-only twin showing the mode you'd switch TO.
// Same useTheme() contract, landing styling only.
function LandingThemeToggle({ className }: { className?: string }) {
  const { dark, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={dark}
      title={dark ? "Light mode" : "Dark mode"}
      className={cn(
        "flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-pill",
        "border border-lp-border bg-lp-surface text-lp-ink-2",
        "transition-colors duration-150 hover:text-lp-accent",
        className
      )}
    >
      {dark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
    </button>
  );
}

function Wordmark({ className }: { className?: string }) {
  return (
    <Link
      href="/"
      aria-label="JobYab — home"
      className={cn("flex flex-none items-center gap-2.5", className)}
    >
      <Image src="/logo.png" alt="JobYab" width={108} height={36} preload className="h-7 w-auto" />
      <span className="font-heading text-lg font-bold text-lp-ink">JobYab</span>
    </Link>
  );
}

// Language selector — "EN" is the only functional entry; the dropdown lists
// the placeholder locales so the affordance reads, but picks keep EN.
const LANGUAGES = ["EN", "DE"] as const;

function LanguageSelector() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        className={cn(
          "flex cursor-pointer items-center gap-1.5 rounded-pill px-3 py-1.5",
          "text-sm font-medium text-lp-ink-2 transition-colors duration-150 hover:text-lp-ink"
        )}
      >
        <GlobeIcon className="h-4 w-4" />
        EN
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Language"
          className="absolute right-0 top-full z-30 mt-2 w-28 overflow-hidden rounded-card border border-lp-border bg-lp-surface shadow-lp-card"
        >
          {LANGUAGES.map((lang) => (
            <li key={lang} role="option" aria-selected={lang === "EN"}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={cn(
                  "w-full cursor-pointer px-3 py-2 text-left text-sm font-medium",
                  lang === "EN" ? "text-lp-accent" : "text-lp-ink-2 hover:text-lp-ink"
                )}
              >
                {lang}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MobileNav({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <header className={cn("relative z-40", className)}>
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5">
        <Wordmark />
        <div className="flex flex-none items-center gap-3">
          <LandingThemeToggle />
          <Link
            href="/register"
            className={cn(PRIMARY_PILL, "h-9 px-4 text-sm")}
          >
            Get started
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            className={cn(
              "flex h-9 w-9 flex-none cursor-pointer flex-col items-center justify-center gap-[5px]",
              "rounded-button border border-lp-border bg-lp-surface"
            )}
          >
            <span aria-hidden="true" className="block h-px w-4 bg-lp-ink" />
            <span aria-hidden="true" className="block h-px w-4 bg-lp-ink" />
            <span aria-hidden="true" className="block h-px w-4 bg-lp-ink" />
          </button>
        </div>
      </div>
      {open && (
        <nav
          aria-label="Mobile"
          className="absolute inset-x-5 top-full z-40 rounded-card border border-lp-border bg-lp-surface p-2 shadow-lp-card"
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block rounded-button px-3 py-2.5 text-sm font-medium text-lp-ink-2 hover:text-lp-ink"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Desktop nav — transparent over --lp-bg, sticky per spec option.
// ---------------------------------------------------------------------------

function DesktopNav() {
  return (
    <header className="sticky top-0 z-40 bg-lp-bg/80 backdrop-blur-sm">
      <div className="mx-auto grid h-16 max-w-[1440px] grid-cols-[1fr_auto_1fr] items-center px-6">
        <Wordmark />
        <nav aria-label="Main" className="flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className="text-sm font-medium text-lp-ink-2 transition-colors duration-150 hover:text-lp-ink"
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="flex flex-none items-center justify-end gap-3">
          <LanguageSelector />
          <LandingThemeToggle />
          <Link href="/login" className={cn(SECONDARY_PILL, "h-9 px-4 text-sm")}>
            Log in
          </Link>
          <Link href="/register" className={cn(PRIMARY_PILL, "h-9 px-4 text-sm")}>
            Get started free <PillArrow />
          </Link>
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero right — the dashboard mockup. Flat (no tilt), rounded-2xl panel,
// --lp-surface fill, --lp-border border, --shadow-lp-card drop shadow.
// Sidebar is the darker --lp-bg navy per the mockup.
// ---------------------------------------------------------------------------

function MiniSidebar() {
  return (
    <aside className="hidden w-[148px] flex-none flex-col justify-between bg-lp-bg p-4 md:flex">
      <div>
        <div className="flex items-center gap-2">
          <Image src="/logo.png" alt="" width={54} height={18} preload className="h-4 w-auto" />
          <span className="font-heading text-sm font-bold text-lp-ink">JobYab</span>
        </div>
        <nav aria-label="Mock dashboard" className="mt-6 flex flex-col gap-1">
          {SIDEBAR_NAV.map((item) => (
            <span
              key={item.label}
              aria-current={item.active ? "page" : undefined}
              className={cn(
                "rounded-button px-3 py-1.5 text-[13px] font-medium",
                item.active
                  ? "bg-lp-primary/15 text-lp-accent"
                  : "text-lp-ink-2"
              )}
            >
              {item.label}
            </span>
          ))}
        </nav>
      </div>
      <div>
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-pill bg-lp-primary/20 text-[11px] font-bold text-lp-accent"
          >
            AW
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold text-lp-ink">
              Alex Weber
            </span>
            <span className="block truncate text-[11px] text-lp-ink-2">alex@jobyab.com</span>
          </span>
        </div>
        <div className="mt-3 flex items-center gap-1.5 border-t border-lp-border pt-3 text-[12px] font-medium text-lp-ink-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-3.5 w-3.5">
            <path d="M15 12H4m0 0 3-3m-3 3 3 3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 4h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Log out
        </div>
      </div>
    </aside>
  );
}

const STAT_CARDS: {
  label: string;
  value: number;
  delta: number;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { label: "Total jobs", value: 128, delta: 24, icon: SearchIcon },
  { label: "Applications", value: 42, delta: 19, icon: PencilSquareIcon },
  { label: "Interviews", value: 12, delta: 33, icon: ClipboardIcon },
  { label: "Offers", value: 3, delta: 200, icon: BriefcaseIcon },
];

function StatRow() {
  return (
    // Container query, not viewport: the mockup's own width decides when four
    // across fit. Four cards need ~720px of row (each ~172px for an
    // untruncated label plus its inline pill), so below that the row stays
    // 2×2 instead of truncating. The @container must sit on a wrapper: a
    // container query on the same element it styles never matches (it reads
    // the nearest ancestor).
    <div className="@container">
      <div className="grid grid-cols-2 gap-3 @[720px]:grid-cols-4">
        {STAT_CARDS.map((card, i) => (
          <StatCard key={card.label} {...card} index={i} variant="landing" />
        ))}
      </div>
    </div>
  );
}

function TypeDistribution() {
  // Theme-picked palette: SVG strokes need literal colors, so read `dark` from
  // useTheme after mount (server render + first client frame use the dark set,
  // which the dark mockup pins; the light correction lands with hydration).
  const { dark } = useTheme();
  const shades = dark ? DONUT_SHADES.dark : DONUT_SHADES.light;
  const data: ArcDonutDatum[] = JOB_TYPE_SPLIT.map((d, i) => ({ ...d, color: shades[i] }));
  return (
    <div className="flex flex-col gap-4 rounded-card border border-lp-border bg-lp-surface p-4">
      <p className="text-sm font-semibold text-lp-ink">Job type distribution</p>
      {/* Ring left, legend right on a fixed auto/1fr split: the ring is its
          fixed 176px, the legend takes the rest. 11px text and a tight gap
          keep the longest label ("Part-time") on one line inside the
          half-width card. On a narrower panel the container query drops this
          card to the full row instead of squeezing the ring or the legend. */}
      <div className="grid grid-cols-[auto_1fr] items-center gap-2">
        <ArcDonut data={data} centerValue={128} centerLabel="jobs" />
        <ul className="flex min-w-0 flex-col gap-2 text-[11px]">
          {JOB_TYPE_SPLIT.map((d, i) => (
            <li key={d.label} className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden="true"
                className="h-2 w-2 flex-none rounded-pill"
                style={{ backgroundColor: shades[i] }}
              />
              <span className="truncate text-lp-ink">{d.label}</span>
              <span className="ml-auto pl-1 font-medium tabular-nums text-lp-ink-2">{d.value}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function DiscoveredChart() {
  return (
    <div className="flex flex-col gap-4 rounded-card border border-lp-border bg-lp-surface p-4">
      <p className="text-sm font-semibold text-lp-ink">Jobs discovered per day</p>
      <div className="flex h-28 gap-2">
        <div className="flex flex-col justify-between pb-5 text-right text-[10px] tabular-nums text-lp-ink-2">
          {[...DISCOVERED_TICKS].reverse().map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
          <span>0</span>
        </div>
        <div className="relative flex-1">
          {/* gridlines at 10 / 20 / 30 */}
          <div aria-hidden="true" className="absolute inset-0">
            {DISCOVERED_TICKS.map((tick) => (
              <div
                key={tick}
                className="absolute inset-x-0 border-t border-lp-border"
                style={{ bottom: `${(tick / BAR_SCALE_MAX) * 100}%` }}
              />
            ))}
          </div>
          <div className="absolute inset-0 flex items-end justify-between gap-[3px]">
            {DISCOVERED_BARS.map((v, i) => (
              <div
                key={i}
                className="w-full rounded-t-[2px] bg-lp-primary"
                style={{ height: `${(v / BAR_SCALE_MAX) * 100}%` }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-between pl-6 text-[10px] text-lp-ink-2">
        {DISCOVERED_BARS.map((_, i) =>
          BAR_INDEX_LABELS[i] ? <span key={i}>{BAR_INDEX_LABELS[i]}</span> : <span key={i} />
        )}
      </div>
    </div>
  );
}

function RecentMatches() {
  return (
    <div className="flex flex-col gap-3 rounded-card border border-lp-border bg-lp-surface p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-lp-ink">Recent matches</p>
        <span className="text-[12px] font-semibold text-lp-accent">View all →</span>
      </div>
      <ul className="flex flex-col">
        {DESKTOP_MATCHES.map((m) => (
          <li
            key={m.role}
            className="flex items-center gap-3 border-t border-lp-border py-2.5 first:border-t-0 first:pt-0 last:pb-0"
          >
            <LogoSquare label={m.initial} bg={m.bg} darkText={m.darkText} className="h-9 w-9 text-[11px]" />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold text-lp-ink">{m.role}</span>
              <span className="block truncate text-[12px] text-lp-ink-2">{m.org}</span>
            </span>
            <span className="ml-auto flex flex-none items-center gap-3">
              <span
                className={cn(
                  "rounded-pill px-2 py-0.5 text-[11px] font-bold tabular-nums",
                  PILL_TONES[m.tone]
                )}
              >
                {m.pct}%
              </span>
              <span className="w-20 text-right text-[11px] text-lp-ink-2">{m.time}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DashboardHeader() {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="font-heading text-lg font-bold text-lp-ink">Welcome back, Alex</p>
        <p className="text-[13px] text-lp-ink-2">Here&apos;s your job search overview.</p>
      </div>
      {/* Decorative mockup content — a span, not a button: the panel is
          aria-hidden, and aria-hidden must not contain focusable elements. */}
      <span className="flex flex-none items-center gap-1.5 rounded-button border border-lp-border bg-lp-surface px-3 py-1.5 text-[12px] font-medium text-lp-ink-2">
        Apr 1, 2025 – May 10, 2025
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </span>
    </div>
  );
}

// The hero-right panel. Sizes are the mockup's compact dashboard proportions;
// the whole mockup is decorative (aria-hidden, static pointer) — a picture of
// the product, not an interactive surface.
function DashboardMockup() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none flex select-none overflow-hidden rounded-2xl border border-lp-border bg-lp-surface shadow-lp-card"
    >
      <MiniSidebar />
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
        <DashboardHeader />
        <StatRow />
        {/* Donut card and bar chart sit side by side (the container query
            stacks them only when the panel is too narrow for the ring plus a
            legend); the donut row keeps ring + legend on one line via
            grid-cols-[auto_1fr] below. Wrapper div again: the query cannot
            live on the element it conditions. */}
        <div className="@container">
          <div className="grid gap-3 @[640px]:grid-cols-2">
            <TypeDistribution />
            <DiscoveredChart />
          </div>
        </div>
        <RecentMatches />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero — desktop and mobile are separate content variants per the spec.
// ---------------------------------------------------------------------------

const HEADLINE_LINE_1 = "Less searching.";
const HEADLINE_LINE_2_MAIN = "More ";
const HEADLINE_LINE_2_ACCENT = "right-fit jobs.";

function HeroBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-pill border border-lp-border bg-lp-surface/60 px-3 py-1 text-[13px] font-medium text-lp-accent">
      <SparklesIcon className="h-3.5 w-3.5" />
      AI-powered job search
    </span>
  );
}

function HeroHeadline() {
  return (
    <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-lp-ink md:text-[60px]">
      {HEADLINE_LINE_1}
      <br />
      {HEADLINE_LINE_2_MAIN}
      <span className="text-lp-accent">{HEADLINE_LINE_2_ACCENT}</span>
    </h1>
  );
}

function HeroActions() {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Link href="/register" className={cn(PRIMARY_PILL, "h-11 px-6 text-[15px]")}>
          Get started free <PillArrow />
        </Link>
        <Link href="/login" className={cn(SECONDARY_PILL, "h-11 px-6 text-[15px]")}>
          Log in
        </Link>
      </div>
      <p className="text-[13px] text-lp-ink-2">Free to start. No card needed.</p>
    </>
  );
}

function SourceLink({ label }: { label: string }) {
  return (
    <span className="text-[13px] font-medium text-lp-ink-2 transition-colors duration-150 hover:text-lp-accent">
      {label}
    </span>
  );
}

function DesktopHero() {
  return (
    <section className="relative overflow-hidden">
      {/* Decorative diagonal glow — a soft gradient shape in --lp-glow, top-right. */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
        <div
          className="absolute -right-40 -top-56 h-[640px] w-[880px] rotate-[-28deg] rounded-full opacity-60 blur-3xl"
          style={{
            background:
              "linear-gradient(160deg, var(--lp-glow) 0%, transparent 62%)",
          }}
        />
      </div>
      <div className="mx-auto grid max-w-[1440px] items-center gap-8 px-6 pb-24 pt-16 lg:grid-cols-[1fr_2fr]">
        <div className="flex flex-col items-start gap-6">
          <Reveal index={0}>
            <HeroBadge />
          </Reveal>
          <Reveal index={1}>
            <HeroHeadline />
          </Reveal>
          <Reveal index={2}>
            <p className="max-w-md text-lg text-lp-ink-2">
              JobYab finds roles, scores your fit, and helps you apply faster.
            </p>
          </Reveal>
          <Reveal index={3}>
            <HeroActions />
          </Reveal>
          <Reveal index={4} className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lp-ink-2">
              Jobs from top sources
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
              {DESKTOP_SOURCES.map((s) => (
                <SourceLink key={s} label={s} />
              ))}
            </div>
          </Reveal>
          <Reveal index={5} className="mt-2 w-full">
            <dl className="flex items-stretch gap-8">
              {HERO_STATS.map((stat, i) => (
                <div
                  key={stat.caption}
                  className={cn("flex flex-col gap-1", i > 0 && "border-l border-lp-border pl-8")}
                >
                  <dt className="order-2 text-[13px] text-lp-ink-2">{stat.caption}</dt>
                  <dd className="order-1 font-heading text-2xl font-bold text-lp-accent">
                    {stat.big}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
        <Reveal index={2}>
          <DashboardMockup />
        </Reveal>
      </div>
    </section>
  );
}

// Mobile hero: same badge/headline/buttons/microcopy; different subhead,
// two-line sources with "+ more", no stats row, and the floating "Job matches"
// card with the "✦ Tailored for you" pill over a decorative ribbon.
function MobileHero() {
  return (
    <section className="relative overflow-hidden px-5 pb-20 pt-10">
      <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
        <div
          className="absolute -right-32 -top-40 h-[420px] w-[560px] rotate-[-28deg] rounded-full opacity-60 blur-3xl"
          style={{
            background:
              "linear-gradient(160deg, var(--lp-glow) 0%, transparent 62%)",
          }}
        />
      </div>

      <div className="flex flex-col items-center gap-5 text-center">
        <Reveal index={0}>
          <HeroBadge />
        </Reveal>
        <Reveal index={1}>
          <HeroHeadline />
        </Reveal>
        <Reveal index={2}>
          <p className="max-w-sm text-base text-lp-ink-2">
            Find roles, score your fit, and apply faster.
          </p>
        </Reveal>
        <Reveal index={3}>
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3">
              <Link href="/register" className={cn(PRIMARY_PILL, "h-11 px-6 text-[15px]")}>
                Get started free <PillArrow />
              </Link>
              <Link href="/login" className={cn(SECONDARY_PILL, "h-11 px-6 text-[15px]")}>
                Log in
              </Link>
            </div>
            <p className="text-[13px] text-lp-ink-2">Free to start. No card needed.</p>
          </div>
        </Reveal>
        <Reveal index={4}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lp-ink-2">
            Jobs from top sources
          </p>
          <div className="mt-3 flex max-w-xs flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {MOBILE_SOURCES_LINE_1.map((s) => (
              <SourceLink key={s} label={s} />
            ))}
            {MOBILE_SOURCES_LINE_2.map((s) => (
              <SourceLink key={s} label={s} />
            ))}
            <span className="text-[13px] font-medium text-lp-ink-2">+ more</span>
          </div>
        </Reveal>
      </div>

      {/* Floating matches card over the decorative ribbon. */}
      <div className="relative mx-auto mt-12 max-w-sm">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 -z-10 h-40 overflow-hidden">
          <svg
            viewBox="0 0 400 160"
            preserveAspectRatio="none"
            className="h-full w-full text-lp-glow"
          >
            <polygon points="0,160 120,0 260,160" fill="currentColor" opacity="0.5" />
            <polygon points="140,160 260,20 400,160" fill="currentColor" opacity="0.35" />
          </svg>
        </div>
        <div className="rounded-card border border-lp-border bg-lp-surface p-4 shadow-lp-card">
          <p className="text-sm font-semibold text-lp-ink">Job matches</p>
          <ul className="mt-3 flex flex-col">
            {MOBILE_MATCHES.map((m) => (
              <li
                key={m.role}
                className="flex items-center gap-3 border-t border-lp-border py-2.5 first:border-t-0 first:pt-0 last:pb-0"
              >
                <LogoSquare label={m.initial} bg={m.bg} darkText={m.darkText} className="h-8 w-8 text-[10px]" />
                <span className="min-w-0 truncate text-[13px] font-semibold text-lp-ink">
                  {m.role}
                </span>
                <span
                  className={cn(
                    "ml-auto rounded-pill px-2 py-0.5 text-[11px] font-bold tabular-nums",
                    PILL_TONES[m.tone]
                  )}
                >
                  {m.pct}%
                </span>
              </li>
            ))}
          </ul>
          <span className="mt-1 inline-block text-[12px] font-semibold text-lp-accent">
            View all matches →
          </span>
        </div>
        <span className="absolute -bottom-4 left-4 inline-flex items-center gap-1.5 rounded-pill border border-lp-border bg-lp-surface px-3 py-1 text-[12px] font-medium text-lp-accent shadow-lp-card">
          <SparklesIcon className="h-3.5 w-3.5" />
          Tailored for you
        </span>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Features. Desktop: 2fr/1fr grid — two large cards + three compact rows.
// Mobile: stacked full-width rows with 1px dividers; the two large cards'
// art changes to the mobile illustrations.
// ---------------------------------------------------------------------------

function SectionHeader() {
  return (
    <div id="features" className="mx-auto max-w-[1440px] scroll-mt-20 px-6">
      <Reveal index={0} className="flex flex-col items-start gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lp-accent">
          Features
        </p>
        <h2 className="font-display text-3xl font-bold tracking-tight text-lp-ink md:text-4xl">
          What matters, in one place.
        </h2>
        <p className="text-base text-lp-ink-2">Focused tools for a faster search.</p>
      </Reveal>
    </div>
  );
}

function FeatureIconSquare({ icon: Icon }: { icon: React.ComponentType<{ className?: string }> }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 flex-none items-center justify-center rounded-button border border-lp-border bg-lp-surface text-lp-accent"
    >
      <Icon className="h-5 w-5" />
    </span>
  );
}

// Gray line placeholder — the mock CV / "Your CV" card's skeleton bars.
function LineBar({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cn("block h-1.5 rounded-pill bg-lp-ink-2/25", className)} />;
}

// Desktop large card 1: copy + mock CV preview with the AI pill. Desktop art
// only — mobile renders its own tilted "Your CV" card. The Generate pill sits
// inside the box at its bottom (it belongs to the preview, not the card edge)
// and points forward — an arrow, not the sparkle the header already uses.
// Sizing is tuned to the 3-equal-column grid: the art and the checklist box
// share ~300px of card inner width, so both are compact.
function CvBuilderArtDesktop() {
  return (
    <div className="flex w-[45%] max-w-[160px] flex-none flex-col">
      <div className="flex flex-1 flex-col rounded-card border border-lp-border bg-lp-surface p-3 shadow-lp-card">
        <p className="text-[12px] font-bold text-lp-ink">Alex Weber</p>
        <p className="text-[10px] leading-tight text-lp-ink-2">Marketing Professional</p>
        <div className="mt-2.5 flex flex-col gap-2">
          <LineBar className="w-full" />
          <LineBar className="w-5/6" />
          <LineBar className="w-full" />
          <LineBar className="w-2/3" />
          <LineBar className="w-3/4" />
        </div>
        <span className="mt-auto inline-flex items-center justify-center gap-1 rounded-pill bg-lp-primary px-2 py-1.5 text-[10px] font-semibold whitespace-nowrap text-white">
          Generate with AI
          <span aria-hidden="true">→</span>
        </span>
      </div>
    </div>
  );
}

// The checklist lives in its own bordered twin of the CV preview box — same
// rounded-card/border/surface recipe — with each check drawn as a filled
// --lp-primary circle carrying a white glyph.
function CvBuilderChecks() {
  return (
    <ul className="flex min-w-0 flex-1 flex-col justify-center gap-3 rounded-card border border-lp-border bg-lp-surface p-3">
      {CV_CHECKS.map((check) => (
        <li key={check} className="flex items-center gap-2 text-[12px] leading-tight text-lp-ink">
          <span
            aria-hidden="true"
            className="flex h-4 w-4 flex-none items-center justify-center rounded-pill bg-lp-primary"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="h-2.5 w-2.5">
              <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="min-w-0">{check}</span>
        </li>
      ))}
    </ul>
  );
}

// Desktop large card 2's art: three compact job rows (Recent-matches style).
function SmartMatchingArt() {
  return (
    <ul className="flex w-full flex-col">
      {DESKTOP_MATCHES.map((m) => (
        <li
          key={m.role}
          className="flex items-center gap-3 border-t border-lp-border py-2.5 first:border-t-0 first:pt-0 last:pb-0"
        >
          <LogoSquare label={m.initial} bg={m.bg} darkText={m.darkText} className="h-8 w-8 text-[10px]" />
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold text-lp-ink">{m.role}</span>
            <span className="block truncate text-[11px] text-lp-ink-2">{m.org}</span>
          </span>
          <span
            className={cn(
              "ml-auto rounded-pill px-2 py-0.5 text-[11px] font-bold tabular-nums",
              PILL_TONES[m.tone]
            )}
          >
            {m.pct}%
          </span>
        </li>
      ))}
    </ul>
  );
}

function DesktopFeatures() {
  return (
    // Three equal columns on desktop — the third stacks the compact rows to
    // the same combined height as the two large cards.
    <div className="mx-auto mt-10 grid max-w-[1440px] gap-5 px-6 lg:grid-cols-3">
      <Reveal index={0} className="flex flex-col">
        <div className="flex flex-1 flex-col gap-5 rounded-card border border-lp-border bg-lp-surface p-6 shadow-lp-card">
          <FeatureIconSquare icon={PencilSquareIcon} />
          <div>
            <h3 className="font-heading text-xl font-bold text-lp-ink">AI CV builder</h3>
            <p className="mt-1 text-[15px] text-lp-ink-2">Tailor your CV in seconds.</p>
          </div>
          <div className="mt-auto flex items-stretch justify-between gap-5">
            <CvBuilderArtDesktop />
            <CvBuilderChecks />
          </div>
        </div>
      </Reveal>
      <Reveal index={1} className="flex flex-col">
        <div className="flex flex-1 flex-col gap-5 rounded-card border border-lp-border bg-lp-surface p-6 shadow-lp-card">
          <FeatureIconSquare icon={SparklesIcon} />
          <div>
            <h3 className="font-heading text-xl font-bold text-lp-ink">Smart matching</h3>
            <p className="mt-1 text-[15px] text-lp-ink-2">See your best-fit roles first.</p>
          </div>
          <div className="mt-auto">
            <SmartMatchingArt />
          </div>
        </div>
      </Reveal>
      <Reveal index={2} className="flex flex-col">
        {/* Three compact rows, deliberately NOT full-bordered cards per spec. */}
        <div className="flex flex-1 flex-col justify-center gap-5">
          {FEATURE_ROWS.map((row, i) => (
            <div
              key={row.title}
              className={cn(
                "flex items-center gap-4 py-2",
                i > 0 && "border-t border-lp-border pt-5"
              )}
            >
              <FeatureIconSquare icon={row.icon} />
              <span>
                <span className="block text-[15px] font-bold text-lp-ink">{row.title}</span>
                <span className="block text-[13px] text-lp-ink-2">{row.subtitle}</span>
              </span>
              <span aria-hidden="true" className="ml-auto text-xl text-lp-ink-2">
                ›
              </span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  );
}

// Mobile feature 1 art: small tilted "Your CV" card, line placeholders only —
// no checklist, no button.
function CvBuilderArtMobile() {
  return (
    <div className="relative mx-auto mt-2 w-40 rotate-[-6deg]">
      <div className="rounded-card border border-lp-border bg-lp-surface p-3.5 shadow-lp-card">
        <p className="text-[12px] font-bold text-lp-ink">Your CV</p>
        <div className="mt-2.5 flex flex-col gap-2">
          <LineBar className="w-full" />
          <LineBar className="w-5/6" />
          <LineBar className="w-full" />
          <LineBar className="w-2/3" />
        </div>
      </div>
    </div>
  );
}

// Mobile feature 2 art: three horizontal fit bars — leading colored
// check-circle, --lp-primary fill on --lp-bar-track (scaleX fill per the
// tracker idiom), trailing % pill tinted to the row's color.
function FitBar({ pct, circle, tone, index }: (typeof MOBILE_FIT_BARS)[number] & { index: number }) {
  const reduced = usePrefersReducedMotion();
  return (
    <div className="flex items-center gap-3">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={circle}
        strokeWidth="2"
        aria-hidden="true"
        className="h-5 w-5 flex-none"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.5 2.5 2.5 5-5.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-pill bg-lp-bar-track">
        <motion.span
          initial={reduced ? false : { scaleX: 0 }}
          animate={{ scaleX: pct / 100 }}
          transition={landingTransition(index, reduced)}
          style={{ originX: 0 }}
          className="block h-full w-full rounded-pill bg-lp-primary"
        />
      </span>
      <span
        className={cn(
          "rounded-pill px-2 py-0.5 text-[11px] font-bold tabular-nums",
          PILL_TONES[tone]
        )}
      >
        {pct}%
      </span>
    </div>
  );
}

function MobileFeatures() {
  return (
    <section className="px-5 pb-16">
      <div className="flex flex-col items-start gap-3">
        <Reveal index={0}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lp-accent">
            Features
          </p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-lp-ink">
            What matters, in one place.
          </h2>
          <p className="text-[15px] text-lp-ink-2">Focused tools for a faster search.</p>
        </Reveal>
      </div>

      <Reveal index={1} className="mt-6">
        <div className="flex flex-col gap-4 rounded-card border border-lp-border bg-lp-surface p-5 shadow-lp-card">
          <FeatureIconSquare icon={PencilSquareIcon} />
          <div>
            <h3 className="font-heading text-lg font-bold text-lp-ink">AI CV builder</h3>
            <p className="mt-1 text-[14px] text-lp-ink-2">
              Turn your experience into a job-ready CV.
            </p>
          </div>
          <CvBuilderArtMobile />
        </div>
      </Reveal>

      <Reveal index={2} className="mt-4">
        <div className="flex flex-col gap-4 rounded-card border border-lp-border bg-lp-surface p-5 shadow-lp-card">
          <FeatureIconSquare icon={SparklesIcon} />
          <div>
            <h3 className="font-heading text-lg font-bold text-lp-ink">Smart matching</h3>
            <p className="mt-1 text-[14px] text-lp-ink-2">Find roles that fit you.</p>
          </div>
          <div className="flex flex-col gap-4">
            {MOBILE_FIT_BARS.map((bar, i) => (
              <FitBar key={bar.pct} {...bar} index={i} />
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal index={3} className="mt-6">
        <ul className="flex flex-col">
          {FEATURE_ROWS.map((row) => (
            <li
              key={row.title}
              className="flex items-center gap-4 border-t border-lp-border py-4 first:border-t-0 first:pt-0 last:pb-0"
            >
              <FeatureIconSquare icon={row.icon} />
              <span>
                <span className="block text-[15px] font-bold text-lp-ink">{row.title}</span>
                <span className="block text-[13px] text-lp-ink-2">{row.subtitle}</span>
              </span>
              <span aria-hidden="true" className="ml-auto text-xl text-lp-ink-2">
                ›
              </span>
            </li>
          ))}
        </ul>
      </Reveal>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CTA banner — MOBILE-ONLY until the user confirms adding it to desktop.
// Dark navy panel with overlapping wave shapes, centered H3 + primary pill.
// ---------------------------------------------------------------------------

function CtaBanner({ className }: { className?: string }) {
  // Wave shapes sit in --lp-glow (light: pale blue, dark: lighter navy) —
  // "slightly lighter" than the panel in both themes, so no theme branch.
  return (
    <section className={cn("px-5 pb-16", className)}>
      <div className="relative overflow-hidden rounded-card border border-lp-border bg-lp-bg p-8 text-center">
        <svg
          aria-hidden="true"
          viewBox="0 0 400 90"
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24 w-full text-lp-glow"
        >
          <path
            d="M0,90 L0,55 C60,30 120,70 200,50 C280,30 340,60 400,40 L400,90 Z"
            fill="currentColor"
            opacity="0.45"
          />
          <path
            d="M0,90 L0,75 C80,55 160,85 240,68 C310,55 360,72 400,62 L400,90 Z"
            fill="currentColor"
            opacity="0.3"
          />
        </svg>
        <div className="relative">
          <h3 className="font-display text-2xl font-bold text-lp-ink">Ready for what&apos;s next?</h3>
          <Link href="/register" className={cn(PRIMARY_PILL, "mt-5 h-11 px-6 text-[15px]")}>
            Get started free <PillArrow />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer — desktop: logo + tagline, link column, two social line-icon
// buttons, copyright line. Mobile: logo + tagline + links only.
// ---------------------------------------------------------------------------

function LinkedInGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 10.5V17" strokeLinecap="round" />
      <circle cx="8" cy="7.3" r="0.9" fill="currentColor" stroke="none" />
      <path d="M12 17v-3.6a2.4 2.4 0 0 1 4.8 0V17" strokeLinecap="round" />
      <path d="M12 10.5V17" strokeLinecap="round" />
    </svg>
  );
}

function XGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 4l16 16M20 4L4 20" strokeLinecap="round" />
    </svg>
  );
}

function DesktopFooter() {
  return (
    <footer className="border-t border-lp-border">
      <div className="mx-auto max-w-[1440px] px-6 pb-8 pt-12">
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div>
            <Wordmark />
            <p className="mt-3 text-sm text-lp-ink-2">A calmer way to job search.</p>
          </div>
          <div className="flex items-center gap-6">
            {FOOTER_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-lp-ink-2 transition-colors duration-150 hover:text-lp-ink"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-2">
              <a
                href="#"
                aria-label="JobYab on LinkedIn"
                className="flex h-9 w-9 items-center justify-center rounded-pill border border-lp-border text-lp-ink-2 transition-colors duration-150 hover:text-lp-ink"
              >
                <LinkedInGlyph />
              </a>
              <a
                href="#"
                aria-label="JobYab on X"
                className="flex h-9 w-9 items-center justify-center rounded-pill border border-lp-border text-lp-ink-2 transition-colors duration-150 hover:text-lp-ink"
              >
                <XGlyph />
              </a>
            </div>
          </div>
        </div>
        <p className="mt-10 border-t border-lp-border pt-6 text-[12px] text-lp-ink-2">
          © 2025 JobYab. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

function MobileFooter() {
  return (
    <footer className="border-t border-lp-border">
      <div className="px-5 pb-10 pt-8">
        <Wordmark />
        <p className="mt-3 text-sm text-lp-ink-2">A calmer way to job search.</p>
        <nav aria-label="Footer" className="mt-6 flex flex-col gap-3">
          {FOOTER_LINKS.map((link) => (
            <a key={link.label} href={link.href} className="text-sm font-medium text-lp-ink-2">
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Root. Both variants render; the breakpoint picks (mobile content differs
// from desktop's — see the mockups). One h1: the shared HeroHeadline is used
// by both, and only one variant is ever displayed.
// ---------------------------------------------------------------------------

export function Landing() {
  const reduced = usePrefersReducedMotion();

  return (
    <div className="landing min-h-screen bg-lp-bg font-sans text-lp-ink antialiased" data-reduced={reduced ? "true" : undefined}>
      {/* Desktop — both variants render; the breakpoint picks. data-variant
          scopes the tests (jsdom has no CSS, so both exist in the DOM). */}
      <div data-variant="desktop" className="hidden md:block">
        <DesktopNav />
        <main>
          <DesktopHero />
          <section id="how-it-works" aria-label="Features" className="scroll-mt-20 pb-24">
            <SectionHeader />
            <DesktopFeatures />
          </section>
          {/* Anchors for the nav links the spec lists. */}
          <div id="pricing" className="hidden scroll-mt-20" />
          <div id="faq" className="hidden scroll-mt-20" />
        </main>
        <DesktopFooter />
      </div>

      {/* Mobile */}
      <div data-variant="mobile" className="md:hidden">
        <MobileNav />
        <main>
          <MobileHero />
          <MobileFeatures />
          <CtaBanner />
        </main>
        <MobileFooter />
      </div>
    </div>
  );
}

